import React, { useState } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogContent, DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, CircularProgress, Alert, TextField
} from '@mui/material';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle } from '../components/StyledComponents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useMutation, gql } from '@apollo/client';
import DepartmentForm from '../components/DepartmentForm';

// GraphQL queries et mutations
const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
      description
      createdAt
    }
  }
`;

const DELETE_DEPARTMENT = gql`
  mutation DeleteDepartment($id: ID!) {
    deleteDepartment(id: $id) {
      success
      message
    }
  }
`;

function Departments() {
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // État pour le département sélectionné
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  // État pour les messages d'erreur
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Récupérer la liste des départements
  const { data, loading, error, refetch } = useQuery(GET_DEPARTMENTS);
  
  // Mutation pour supprimer un département
  const [deleteDepartment, { loading: deleteLoading }] = useMutation(DELETE_DEPARTMENT, {
    onCompleted: (data) => {
      if (data.deleteDepartment.success) {
        setOpenDeleteDialog(false);
        setSelectedDepartment(null);
        setErrorMessage('');
        refetch();
      } else {
        setErrorMessage(data.deleteDepartment.message || "Une erreur est survenue lors de la suppression");
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression:", error);
      setErrorMessage(`Erreur: ${error.message}`);
    }
  });
  
  // Gérer l'ouverture du dialogue de création
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };
  
  // Gérer la fermeture du dialogue de création
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  
  // Gérer l'ouverture du dialogue de modification
  const handleOpenEditDialog = (department) => {
    setSelectedDepartment(department);
    setOpenEditDialog(true);
  };
  
  // Gérer la fermeture du dialogue de modification
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedDepartment(null);
  };
  
  // Gérer l'ouverture du dialogue de suppression
  const handleOpenDeleteDialog = (department) => {
    setSelectedDepartment(department);
    setOpenDeleteDialog(true);
  };
  
  // Gérer la fermeture du dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedDepartment(null);
  };
  
  // Gérer la suppression d'un département
  const handleDeleteDepartment = () => {
    if (selectedDepartment) {
      deleteDepartment({
        variables: {
          id: selectedDepartment.id
        }
      });
    }
  };
  
  // Gérer le succès de la création d'un département
  const handleCreateSuccess = () => {
    setOpenCreateDialog(false);
    refetch();
  };
  
  // Gérer le succès de la modification d'un département
  const handleEditSuccess = () => {
    setOpenEditDialog(false);
    setSelectedDepartment(null);
    refetch();
  };

  const filteredDepartments = (data?.departments || []).filter((department) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      department.name?.toLowerCase().includes(query) ||
      department.description?.toLowerCase().includes(query)
    );
  });
  
  // Afficher un indicateur de chargement si les données sont en cours de chargement
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Afficher un message d'erreur si une erreur s'est produite
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Erreur lors du chargement des départements: {error.message}
          </Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Départements
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Rechercher"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Rechercher par nom ou description..."
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Nouveau Département
          </Button>
        </Box>
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date de création</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDepartments.map((department, index) => (
                <TableRow 
                  key={department.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                    ...tableRowHoverStyles
                  }}
                >
                  <TableCell>{department.name}</TableCell>
                  <TableCell>{department.description || '-'}</TableCell>
                  <TableCell>{department.createdAt ? new Date(department.createdAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditDialog(department)}
                      sx={iconButtonStyles}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(department)}
                      sx={iconButtonStyles}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredDepartments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucun département trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
      
      {/* Dialogue de création de département */}
      <Dialog 
        open={openCreateDialog} 
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Créer un nouveau département</StyledDialogTitle>
        <DialogContent>
          <DepartmentForm onSuccess={handleCreateSuccess} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de modification de département */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Modifier le département</StyledDialogTitle>
        <DialogContent>
          <DepartmentForm 
            onSuccess={handleEditSuccess} 
            initialData={selectedDepartment}
            isEdit={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de suppression de département */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <DialogContent>
          {selectedDepartment && (
            <Typography>
              Êtes-vous sûr de vouloir supprimer le département {selectedDepartment.name} ?
            </Typography>
          )}
          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDeleteDepartment} 
            color="error"
            disabled={deleteLoading || !selectedDepartment}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Departments; 
